// src/components/RegistrarContratos.jsx
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "Contratos";

const isValidObjectId = (v) => /^[0-9a-fA-F]{24}$/.test(String(v || ""));

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const RegistrarContratos = () => {
  const { id: instructorId, idcontrato } = useParams();
  const navigate = useNavigate();

  const isEdit = useMemo(() => Boolean(idcontrato), [idcontrato]);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("tab1");

  const [contrato, setContrato] = useState("");
  const [rp, setRp] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [valorContrato, setValorContrato] = useState("");
  const [numeroCuenta, setNumeroCuenta] = useState("");
  const [tipoCuenta, setTipoCuenta] = useState("");
  const [banco, setBanco] = useState("");
  const [municipioSede, setMunicipioSede] = useState("");
  const [estado, setEstado] = useState("");

  const [regionales, setRegionales] = useState([]);
  const [regional, setRegional] = useState("");
  const [centros, setCentros] = useState([]);
  const [centroFormacion, setCentroFormacion] = useState("");

  const [objetoContrato, setObjetoContrato] = useState("");
  const [valorFormaPago, setValorFormaPago] = useState("");

  const [supervisor, setSupervisor] = useState({
    documento: "",
    nombre: "",
    cargo: "",
  });

  const [subdirector, setSubdirector] = useState({
    documento: "",
    nombre: "",
    cargo: "",
  });

  const authConfig = useCallback(() => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  const getModuloContratos = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloContratos = useMemo(() => getModuloContratos(), []);

  const puedeCrear =
    moduloContratos?.puedeCrear === true ||
    moduloContratos?.crear === true ||
    moduloContratos?.create === true;

  const puedeActualizar =
    moduloContratos?.puedeActualizar === true ||
    moduloContratos?.actualizar === true ||
    moduloContratos?.editar === true ||
    moduloContratos?.update === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const cleanData = useCallback(() => {
    setContrato("");
    setRp("");
    setFechaInicio("");
    setFechaFin("");
    setValorContrato("");
    setNumeroCuenta("");
    setTipoCuenta("");
    setBanco("");
    setMunicipioSede("");
    setEstado("");
    setRegional("");
    setCentroFormacion("");
    setCentros([]);
    setObjetoContrato("");
    setValorFormaPago("");
    setSupervisor({ documento: "", nombre: "", cargo: "" });
    setSubdirector({ documento: "", nombre: "", cargo: "" });
    setActiveTab("tab1");
  }, []);

  const getData = useCallback(async () => {
    if (!isEdit || !idcontrato) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/contratos/listid/${idcontrato}`,
        authConfig()
      );

      const c = data?.contrato;

      setContrato(c?.contrato || "");
      setRp(c?.rp || "");
      setFechaInicio(c?.fechaInicio?.substring(0, 10) || "");
      setFechaFin(c?.fechaFin?.substring(0, 10) || "");
      setValorContrato(c?.valorContrato || "");
      setNumeroCuenta(c?.numeroCuenta || "");
      setTipoCuenta(c?.tipoCuenta || "");
      setBanco(c?.banco || "");
      setMunicipioSede(c?.municipioSede || "");
      setEstado(c?.estado || "");

      setRegional(c?.regional?._id || c?.regional || "");
      setCentroFormacion(c?.centroFormacion?._id || c?.centroFormacion || "");

      setObjetoContrato(c?.objetoContrato || "");
      setValorFormaPago(c?.valorFormaPago || "");

      setSupervisor(c?.supervisor || { documento: "", nombre: "", cargo: "" });
      setSubdirector(c?.subdirector || { documento: "", nombre: "", cargo: "" });
      setActiveTab("tab1");
    } catch (error) {
      console.error("Error al cargar el contrato:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al cargar contrato",
        text:
          error.response?.data?.message ||
          "Error inesperado al cargar datos.",
      });
    } finally {
      setLoading(false);
    }
  }, [idcontrato, isEdit, authConfig, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    const fetchRegionales = async () => {
      try {
        const res = await axios.get("/api/regionales/listall", authConfig());

        if (res.data.ok) {
          const regionalesOrdenadas = (res.data.regionales || []).sort((a, b) =>
            a.nombreRegional.localeCompare(b.nombreRegional)
          );

          setRegionales(regionalesOrdenadas);
        }
      } catch (error) {
        console.error("Error al obtener las regionales:", error);

        if (manejarErrorSesion(error)) return;
      }
    };

    fetchRegionales();
  }, [authConfig, manejarErrorSesion]);

  useEffect(() => {
    const fetchCentros = async () => {
      if (!regional) {
        setCentros([]);
        setCentroFormacion("");
        return;
      }

      try {
        const res = await axios.get(
          `/api/centrosdeformacion/listxregional/${regional}`,
          authConfig()
        );

        if (res.data.ok) {
          setCentros(res.data?.regional?.centros || []);
        }
      } catch (error) {
        console.error("Error al obtener centros:", error);

        if (manejarErrorSesion(error)) return;
      }
    };

    fetchCentros();
  }, [regional, authConfig, manejarErrorSesion]);

  const saveContrato = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tienes permisos para crear contratos.",
        "warning"
      );
      return;
    }

    try {
      if (!isValidObjectId(instructorId)) {
        return Swal.fire({
          icon: "warning",
          title: "Falta instructor",
          text: "No llegó un id válido del instructor en la URL.",
        });
      }

      const newContrato = {
        contrato,
        rp,
        fechaInicio,
        fechaFin,
        valorContrato,
        numeroCuenta,
        tipoCuenta,
        banco,
        municipioSede,
        estado,
        regional,
        centroFormacion,
        supervisor,
        subdirector,
        objetoContrato,
        valorFormaPago,
        instructorObjectId: instructorId,
      };

      setLoading(true);

      await axios.post("/api/contratos/add", newContrato, authConfig());

      await Swal.fire({
        icon: "success",
        title: "¡Contrato registrado con éxito!",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error en saveContrato:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al guardar el contrato.",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateContrato = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tienes permisos para actualizar contratos.",
        "warning"
      );
      return;
    }

    try {
      const contratoActualizado = {
        contrato,
        rp,
        fechaInicio,
        fechaFin,
        valorContrato,
        numeroCuenta,
        tipoCuenta,
        banco,
        municipioSede,
        estado,
        regional,
        centroFormacion,
        supervisor,
        subdirector,
        objetoContrato,
        valorFormaPago,
      };

      setLoading(true);

      await axios.put(
        `/api/contratos/update/${idcontrato}`,
        contratoActualizado,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "¡Contrato actualizado con éxito!",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error al actualizar contrato:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: error.response?.data?.message || "Error inesperado",
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (isEdit) {
      await updateContrato();
    } else {
      await saveContrato();
    }
  };

  const disabledAll = loading || (isEdit ? !puedeActualizar : !puedeCrear);

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Contrato" : "Registrar Contrato"}
                </h5>

                <small className="text-secondary">
                  Completa la información contractual, responsables y datos
                  adicionales.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={cleanData}
                  disabled={disabledAll}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="submit"
                  form="form-contrato"
                  className="btn btn-dark"
                  disabled={disabledAll}
                  title={isEdit ? "Actualizar Contrato" : "Guardar Contrato"}
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                      } me-2`}
                  ></i>
                  {isEdit ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "tab1" ? "active" : ""}`}
                  onClick={() => setActiveTab("tab1")}
                  disabled={loading}
                >
                  <i className="fa-solid fa-file-contract me-2"></i>
                  Contrato
                </button>
              </li>

              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "tab2" ? "active" : ""}`}
                  onClick={() => setActiveTab("tab2")}
                  disabled={loading}
                >
                  <i className="fa-solid fa-user-tie me-2"></i>
                  Supervisor / Subdirector
                </button>
              </li>

              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "tab3" ? "active" : ""}`}
                  onClick={() => setActiveTab("tab3")}
                  disabled={loading}
                >
                  <i className="fa-solid fa-circle-info me-2"></i>
                  Adicional
                </button>
              </li>
            </ul>

            <form id="form-contrato" onSubmit={actions}>
              {activeTab === "tab1" && (
                <div className="border rounded-4 p-3 p-md-4 mb-3">
                  <div className="fw-semibold mb-2">
                    Información del Contrato
                  </div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">Contrato</label>
                      <input
                        type="text"
                        className="form-control"
                        value={contrato}
                        onChange={(e) => setContrato(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">RP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={rp}
                        onChange={(e) => setRp(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">
                        Fecha Inicio
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">
                        Fecha Fin
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">
                        Valor del Contrato ($)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={valorContrato}
                        onChange={(e) => setValorContrato(e.target.value)}
                        required
                        min="0"
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">
                        Número de Cuenta
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={numeroCuenta}
                        onChange={(e) => setNumeroCuenta(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">
                        Tipo de Cuenta
                      </label>
                      <select
                        className="form-select"
                        value={tipoCuenta}
                        onChange={(e) => setTipoCuenta(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        <option value="Ahorros">Ahorros</option>
                        <option value="Corriente">Corriente</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Banco</label>
                      <select
                        className="form-select"
                        value={banco}
                        onChange={(e) => setBanco(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        <option value="Bancolombia">Bancolombia</option>
                        <option value="Banco de Bogotá">Banco de Bogotá</option>
                        <option value="Banco Davivienda">
                          Banco Davivienda
                        </option>
                        <option value="Banco de Occidente">
                          Banco de Occidente
                        </option>
                        <option value="Banco AV Villas">Banco AV Villas</option>
                        <option value="Banco Agrario">Banco Agrario</option>
                        <option value="Banco Popular">Banco Popular</option>
                        <option value="Banco Itaú">Banco Itaú</option>
                        <option value="Banco BBVA">Banco BBVA</option>
                        <option value="Banco Falabella">
                          Banco Falabella
                        </option>
                        <option value="Banco Pichincha">
                          Banco Pichincha
                        </option>
                        <option value="Banco Finandina">
                          Banco Finandina
                        </option>
                        <option value="Banco Caja Social">
                          Banco Caja Social
                        </option>
                        <option value="Banco GNB Sudameris">
                          Banco GNB Sudameris
                        </option>
                        <option value="Banco Coopcentral">
                          Banco Coopcentral
                        </option>
                        <option value="Banco Serfinanza">
                          Banco Serfinanza
                        </option>
                        <option value="Nequi">Nequi</option>
                        <option value="Daviplata">Daviplata</option>
                        <option value="Movii">Movii</option>
                        <option value="Lulo Bank">Lulo Bank</option>
                        <option value="Nu Colombia">Nu Colombia</option>
                        <option value="RappiPay">RappiPay</option>
                        <option value="Banco W">Banco W</option>
                        <option value="Banco Mundo Mujer">
                          Banco Mundo Mujer
                        </option>
                        <option value="Scotiabank Colpatria">
                          Scotiabank Colpatria
                        </option>
                      </select>
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">
                        Municipio Sede
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={municipioSede}
                        onChange={(e) => setMunicipioSede(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Estado</label>
                      <select
                        className="form-select"
                        value={estado}
                        onChange={(e) => setEstado(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        <option value="Activo">Activo</option>
                        <option value="Finalizado">Finalizado</option>
                        <option value="Suspendido">Suspendido</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Regional</label>
                      <select
                        className="form-select"
                        value={regional}
                        onChange={(e) => setRegional(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        {regionales.map((reg) => (
                          <option key={reg._id} value={reg._id}>
                            {reg.nombreRegional}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        Centro de Formación
                      </label>
                      <select
                        className="form-select"
                        value={centroFormacion}
                        onChange={(e) => setCentroFormacion(e.target.value)}
                        required
                        disabled={disabledAll || !regional}
                      >
                        <option value="">Seleccione…</option>
                        {centros.map((centro) => (
                          <option key={centro._id} value={centro._id}>
                            {centro.nombreCentro}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "tab2" && (
                <div className="border rounded-4 p-3 p-md-4 mb-3">
                  <div className="fw-semibold mb-2">
                    Supervisor del Contrato
                  </div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">
                        Documento
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={supervisor.documento}
                        onChange={(e) =>
                          setSupervisor({
                            ...supervisor,
                            documento: e.target.value,
                          })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-5">
                      <label className="form-label fw-semibold">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={supervisor.nombre}
                        onChange={(e) =>
                          setSupervisor({
                            ...supervisor,
                            nombre: e.target.value,
                          })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Cargo</label>
                      <input
                        type="text"
                        className="form-control"
                        value={supervisor.cargo}
                        onChange={(e) =>
                          setSupervisor({
                            ...supervisor,
                            cargo: e.target.value,
                          })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="fw-semibold mb-2">
                    Subdirector(a) Encargado(a) (E)
                  </div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">
                        Documento
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={subdirector.documento}
                        onChange={(e) =>
                          setSubdirector({
                            ...subdirector,
                            documento: e.target.value,
                          })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-5">
                      <label className="form-label fw-semibold">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={subdirector.nombre}
                        onChange={(e) =>
                          setSubdirector({
                            ...subdirector,
                            nombre: e.target.value,
                          })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Cargo</label>
                      <input
                        type="text"
                        className="form-control"
                        value={subdirector.cargo}
                        onChange={(e) =>
                          setSubdirector({
                            ...subdirector,
                            cargo: e.target.value,
                          })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "tab3" && (
                <div className="border rounded-4 p-3 p-md-4 mb-3">
                  <div className="fw-semibold mb-2">
                    Información Adicional
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold">
                        Objeto del Contrato
                      </label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={objetoContrato}
                        onChange={(e) => setObjetoContrato(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">
                        Valor y Forma de Pago
                      </label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={valorFormaPago}
                        onChange={(e) => setValorFormaPago(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex flex-nowrap gap-2 justify-content-end pt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={cleanData}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="submit"
                  className="btn btn-dark"
                  disabled={disabledAll}
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                      } me-2`}
                  ></i>
                  {isEdit ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarContratos;